// Voice-announcement preferences. Lives in localStorage (per-device,
// like volume sliders typically are — not synced through the user row).
//
// Storage layout:
//   localStorage["rust-sight:voice-prefs:v1"] = JSON of `VoicePreferences`
//
// We intentionally don't enforce a schema at the storage layer. Reads
// tolerate missing fields and old shapes; writes overwrite the whole
// blob. If we ever need a v2, the key prefix changes and we either
// migrate inline or drop the old key.

import type { RulePreference } from "./types";

export interface VoicePreferences {
  /** Master on/off for the entire feature. */
  enabled: boolean;
  /** Volume 0..1 applied to every utterance. */
  volume: number;
  /**
   * SpeechSynthesisVoice.voiceURI of the selected voice, or null to let
   * pickDefaultVoice run each time the voice list changes.
   */
  voiceURI: string | null;
  /** Per-rule preferences, keyed by AnnouncementRule.id. */
  rules: Record<string, RulePreference>;
  /**
   * Once the user has clicked "Test voice" or otherwise confirmed
   * the autoplay-policy unlock, we remember it so the banner doesn't
   * keep appearing.
   */
  audioUnlocked: boolean;
}

export const VOICE_PREFS_KEY = "rust-sight:voice-prefs:v1";

export const DEFAULT_VOICE_PREFS: VoicePreferences = {
  enabled: false,         // opt-in
  volume: 1.0,
  voiceURI: null,
  rules: {},
  audioUnlocked: false,
};

export function readVoicePrefs(): VoicePreferences {
  if (typeof window === "undefined") return DEFAULT_VOICE_PREFS;
  try {
    const raw = window.localStorage.getItem(VOICE_PREFS_KEY);
    if (!raw) return DEFAULT_VOICE_PREFS;
    const parsed = JSON.parse(raw) as Partial<VoicePreferences>;
    return {
      enabled: typeof parsed.enabled === "boolean" ? parsed.enabled : DEFAULT_VOICE_PREFS.enabled,
      volume: Number.isFinite(parsed.volume) && (parsed.volume as number) >= 0 && (parsed.volume as number) <= 1
        ? (parsed.volume as number)
        : DEFAULT_VOICE_PREFS.volume,
      voiceURI: typeof parsed.voiceURI === "string" ? parsed.voiceURI : null,
      rules: parsed.rules && typeof parsed.rules === "object" ? parsed.rules as Record<string, RulePreference> : {},
      audioUnlocked: typeof parsed.audioUnlocked === "boolean" ? parsed.audioUnlocked : false,
    };
  } catch {
    return DEFAULT_VOICE_PREFS;
  }
}

export function writeVoicePrefs(prefs: VoicePreferences): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VOICE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Quota exceeded / private mode — degrade silently. The settings UI
    // can probe the same key on next mount and see the value didn't
    // stick if it matters; for now, accept the no-op.
  }
}

export function patchVoicePrefs(patch: Partial<VoicePreferences>): VoicePreferences {
  const current = readVoicePrefs();
  const next: VoicePreferences = { ...current, ...patch };
  writeVoicePrefs(next);
  return next;
}

export function setRulePref(ruleId: string, pref: RulePreference): VoicePreferences {
  const current = readVoicePrefs();
  const next: VoicePreferences = {
    ...current,
    rules: { ...current.rules, [ruleId]: pref },
  };
  writeVoicePrefs(next);
  return next;
}
